import { inject, Injectable } from '@angular/core';
import { MerchantService, UpdateMerchantRequest } from '@app/resources/services/merchant.service';
import { Observable } from 'rxjs';
import { Merchant } from '@shared/models/merchant.model';
import { MerchantRepresentativeAccess } from '@shared/models/merchant-access.model';
import { map } from 'rxjs/operators';
import { CreateEnrollmentRequest, EnrollmentService } from '@app/resources/services/enrollment.service';
import { EmailService, SendContactCustomerSupportEmailRequest } from '@app/resources/services/email.service';

@Injectable()
export class MerchantsApiService {
  private readonly merchantService = inject(MerchantService);
  private readonly enrollmentService = inject(EnrollmentService);
  private readonly emailService = inject(EmailService);

  getMerchants(coalitionId: number): Observable<Merchant[]> {
    return this.merchantService.findAll(coalitionId).pipe(
      map((result: any) => {
        if (result !== null && result.$values !== null) {
          return result.$values.map(mapToMerchant);
        }
        return [];
      })
    );
  }

  getById(merchantId: number): Observable<Merchant | null> {
    return this.merchantService
      .findById(merchantId)
      .pipe(map((result: any) => (result ? mapToMerchant(result) : null)));
  }

  update(merchantId: number, updateMerchantRequest: UpdateMerchantRequest): Observable<Merchant | null> {
    return this.merchantService
      .update(merchantId, updateMerchantRequest)
      .pipe(map((result: any) => (result ? mapToMerchant(result) : null)));
  }

  getAccessByMerchantId(merchantId: number): Observable<MerchantRepresentativeAccess[]> {
    return this.merchantService.findAccessByMerchantId(merchantId).pipe(
      map((result: any) => {
        const values = result?.$values ?? [];
        return values.map(mapToMerchantRepresentativeAccess);
      })
    );
  }

  enrollMerchant(createEnrollmentRequest: CreateEnrollmentRequest) {
    return this.enrollmentService.create(createEnrollmentRequest);
  }

  sendContactSupportEmail(sendContactCustomerSupportRequest: SendContactCustomerSupportEmailRequest) {
    return this.emailService.sendContactCustomerSupport(sendContactCustomerSupportRequest);
  }
}

function mapToMerchant(data: any): Merchant {
  return {
    MerchantId: data.merchantId,
    Name: data.name,
    BasePrice: data.basePrice ?? undefined,
    PerSmsprice: data.perSmsprice ?? undefined,
    PerLocationPrice: data.perLocationPrice ?? undefined,
    PerDevicePrice: data.perDevicePrice ?? undefined,
    PerCampaignPrice: data.perCampaignPrice ?? undefined,
    SalesRepresentativeId: data.salesRepresentativeId ?? null,
    SalesRepresentativeName: data.salesRepresentativeName ?? null,
    HasPhoneNumberAttached: data.hasPhoneNumberAttached ?? false,
    HasAccess: data.hasAccess ?? true,
  };
}

function mapToMerchantRepresentativeAccess(data: any): MerchantRepresentativeAccess {
  return {
    MerchantAccessId: data.merchantAccessId,
    RepresentativeId: data.representativeId,
    RepresentativeName: data.representativeName,
    EmailAddress: data.emailAddress ?? undefined,
    IsPrimary: data.isPrimary,
  };
}
